interface ShopifyVariant {
  id: number;
  title: string;
  price: string;
  available: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

interface ShopifyProduct {
  id: number;
  title: string;
  handle: string;
  body_html: string | null;
  images: Array<{
    id: number;
    src: string;
    alt: string | null;
  }>;
  variants: ShopifyVariant[];
}

interface ShopifyAdminResponse {
  products: ShopifyProduct[];
}

const DEFAULT_SHOPIFY = {
  shopDomain: 'mandatory-outfitter.myshopify.com',
  accessToken: 'c0f802098282166caa117dfc633d8199',
} as const;

// Clean up product titles before display
const cleanProductTitle = (title: string): string => {
  return title.replace(/\s*2\s*$/i, '').trim();
};

class ShopifyService {
  private shopDomain: string = '';
  private accessToken: string = '';
  private isConfigured: boolean = false;

  configure(shopDomain: string, accessToken: string) {
    // Normalize domain: remove scheme/paths/credentials and map admin URL to myshopify domain
    let domain = shopDomain.trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^([^@]+)@/, '') // remove credentials like user@
      .replace(/\/$/, '')
      .replace(/\/.*/, '');

    // If user pasted an admin URL like admin.shopify.com/store/handle -> handle.myshopify.com
    const adminMatch = domain.match(/^admin\.shopify\.com\/store\/([^/?#]+)/i);
    if (adminMatch) {
      domain = `${adminMatch[1]}.myshopify.com`;
    }

    this.shopDomain = domain;
    this.accessToken = accessToken.trim();
    this.isConfigured = !!this.shopDomain && !!this.accessToken;
    console.info('[ShopifyService] Configured', { domain: this.shopDomain, ready: this.isConfigured });
  }

  isReady(): boolean {
    return this.isConfigured && !!this.shopDomain && !!this.accessToken;
  }

  async fetchProducts(limit: number = 250): Promise<ShopifyProduct[]> {
    if (!this.isReady()) {
      // Ensure default configuration if not yet configured
      if (DEFAULT_SHOPIFY.shopDomain && DEFAULT_SHOPIFY.accessToken) {
        this.configure(DEFAULT_SHOPIFY.shopDomain, DEFAULT_SHOPIFY.accessToken);
      }
    }
    if (!this.isReady()) {
      // Return mock data when not configured
      return this.getMockProducts();
    }

    try {
      const query = `
        query Products($first: Int!) {
          products(first: $first, sortKey: CREATED_AT, reverse: true) {
            edges {
              node {
                id
                handle
                title
                description
                createdAt
                updatedAt
                images(first: 5) { edges { node { id url altText } } }
                variants(first: 50) { 
                  edges { 
                    node { 
                      id 
                      title 
                      price { amount } 
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                    } 
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${this.shopDomain}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken,
        },
        body: JSON.stringify({ query, variables: { first: Math.min(limit, 100) } })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const edges = data?.data?.products?.edges ?? [];
      const mapped: ShopifyProduct[] = edges.map((e: any, idx: number) => {
        const n = e.node;
        const firstImg = n.images?.edges?.[0]?.node;
        const firstVar = n.variants?.edges?.[0]?.node;
        const toNumId = (gid: string | undefined, fallback: number) => {
          const s = gid?.toString() ?? '';
          const tail = s.split('/').pop();
          const parsed = tail ? parseInt(tail, 10) : NaN;
          return Number.isFinite(parsed) ? parsed : fallback;
        };
        return {
          id: toNumId(n.id, idx + 1),
          title: cleanProductTitle(n.title),
          handle: n.handle,
          body_html: n.description || null,
          images: n.images?.edges?.map((imgEdge: any, imgIdx: number) => ({
            id: toNumId(imgEdge.node.id, imgIdx + 1),
            src: imgEdge.node.url,
            alt: imgEdge.node.altText || null
          })) || [],
          variants: n.variants?.edges?.map((varEdge: any, varIdx: number) => ({
            id: toNumId(varEdge.node.id, varIdx + 1),
            title: varEdge.node.title || 'Default',
            price: String(varEdge.node.price?.amount ?? ''),
            available: !!varEdge.node.availableForSale,
            selectedOptions: varEdge.node.selectedOptions || []
          })) || []
        } as ShopifyProduct;
      });

      return mapped;
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      // Fallback to mock data on error
      return this.getMockProducts();
    }
  }

  async fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
    if (!this.isReady()) {
      if (DEFAULT_SHOPIFY.shopDomain && DEFAULT_SHOPIFY.accessToken) {
        this.configure(DEFAULT_SHOPIFY.shopDomain, DEFAULT_SHOPIFY.accessToken);
      }
    }
    if (!this.isReady()) {
      return null;
    }

    try {
      const query = `
        query ProductByHandle($handle: String!) {
          productByHandle(handle: $handle) {
            id
            handle
            title
            description
            images(first: 5) { edges { node { id url altText } } }
            variants(first: 50) { 
              edges { 
                node { 
                  id 
                  title 
                  price { amount } 
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                } 
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${this.shopDomain}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken,
        },
        body: JSON.stringify({ query, variables: { handle } })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      const n = json?.data?.productByHandle;
      if (!n) return null;

      const firstImg = n.images?.edges?.[0]?.node;
      const firstVar = n.variants?.edges?.[0]?.node;
      const toNumId = (gid: string | undefined, fallback: number) => {
        const s = gid?.toString() ?? '';
        const tail = s.split('/').pop();
        const parsed = tail ? parseInt(tail, 10) : NaN;
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const product: ShopifyProduct = {
        id: toNumId(n.id, 1),
        title: cleanProductTitle(n.title),
        handle: n.handle,
        body_html: n.description || null,
        images: n.images?.edges?.map((imgEdge: any, imgIdx: number) => ({
          id: toNumId(imgEdge.node.id, imgIdx + 1),
          src: imgEdge.node.url,
          alt: imgEdge.node.altText || null
        })) || [],
        variants: n.variants?.edges?.map((varEdge: any, varIdx: number) => ({
          id: toNumId(varEdge.node.id, varIdx + 1),
          title: varEdge.node.title || 'Default',
          price: String(varEdge.node.price?.amount ?? ''),
          available: !!varEdge.node.availableForSale,
          selectedOptions: varEdge.node.selectedOptions || []
        })) || []
      };

      return product;
    } catch (error) {
      console.error('Error fetching Shopify product:', error);
      return null;
    }
  }

  async createCheckout(cartItems: any[]): Promise<string | null> {
    console.log('[ShopifyService] Creating checkout for items:', cartItems);
    
    if (!this.isReady()) {
      console.error('[ShopifyService] Shopify not configured');
      return null;
    }

    try {
      // For now, let's use a simpler approach - redirect to Shopify store
      // Since Storefront API checkout creation requires specific permissions
      
      // Build cart URL for Shopify store
      const cartUrl = `https://${this.shopDomain}/cart/`;
      
      // Add items to cart via URL parameters (Shopify cart permalink format)
      const cartParams = cartItems.map(item => {
        // Extract variant ID number from the full ID string
        const variantIdMatch = item.variantId?.match(/\/(\d+)$/);
        const variantId = variantIdMatch ? variantIdMatch[1] : null;
        
        console.log('[ShopifyService] Processing item:', { 
          name: item.name, 
          variantId: item.variantId, 
          extractedId: variantId,
          quantity: item.quantity 
        });
        
        if (variantId) {
          return `${variantId}:${item.quantity}`;
        }
        return null;
      }).filter(Boolean);

      if (cartParams.length === 0) {
        console.error('[ShopifyService] No valid variant IDs found');
        return null;
      }

      const shopifyCartUrl = `${cartUrl}${cartParams.join(',')}`;

      // Append active discount code from the spin wheel, if any & not expired
      let finalUrl = shopifyCartUrl;
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('wheel-active-code') : null;
        if (raw) {
          const parsed = JSON.parse(raw) as { code: string; expiresAt: number };
          if (parsed?.code && parsed.expiresAt > Date.now()) {
            finalUrl += `?discount=${encodeURIComponent(parsed.code)}`;
          } else {
            localStorage.removeItem('wheel-active-code');
          }
        }
      } catch (e) {
        console.warn('[ShopifyService] discount code parse failed', e);
      }

      console.log('[ShopifyService] Generated cart URL:', finalUrl);
      return finalUrl;
      
    } catch (error) {
      console.error('[ShopifyService] Error creating checkout:', error);
      return null;
    }
  }

  private getMockProducts(): ShopifyProduct[] {
    return [
      {
        id: 1,
        title: 'MANDATORY Street Cap',
        handle: 'mandatory-street-cap',
        body_html: 'Premium Streetwear Collection',
        images: [{
          id: 1,
          src: '/lovable-uploads/10016ec0-8a5b-4d7a-b38d-a31c33f1edb1.png',
          alt: 'MANDATORY Street Cap'
        }],
        variants: [{
          id: 1,
          title: 'Default Title',
          price: '35.00',
          available: true,
          selectedOptions: [
            { name: 'Color', value: 'Black' },
            { name: 'Size', value: 'M' }
          ]
        }]
      },
      {
        id: 2,
        title: 'Urban Graffiti Hoodie',
        handle: 'urban-graffiti-hoodie',
        body_html: 'Premium Streetwear Collection',
        images: [{
          id: 2,
          src: '/lovable-uploads/26325ec1-0149-4dc1-97eb-cb630b1bcbc4.png',
          alt: 'Urban Graffiti Hoodie'
        }],
        variants: [{
          id: 2,
          title: 'Default Title',
          price: '89.99',
          available: true,
          selectedOptions: [
            { name: 'Color', value: 'Black' },
            { name: 'Size', value: 'L' }
          ]
        }]
      },
      {
        id: 3,
        title: 'Street Art Backpack',
        handle: 'street-art-backpack',
        body_html: 'Premium Streetwear Collection',
        images: [{
          id: 3,
          src: '/lovable-uploads/cc283dd9-df6d-403a-a4c6-c6ff9ef6c19c.png',
          alt: 'Street Art Backpack'
        }],
        variants: [{
          id: 3,
          title: 'Default Title',
          price: '59.99',
          available: true,
          selectedOptions: [
            { name: 'Color', value: 'Gray' },
            { name: 'Size', value: 'M' }
          ]
        }]
      }
    ];
  }
}

// Export singleton instance
export const shopifyService = new ShopifyService();
export type { ShopifyProduct, ShopifyVariant };